import {
  type DatabaseBlockDataSource,
  propertyType,
  t,
} from '@blocksuite/affine/blocks/database';
import zod from 'zod';

import {
  getRelationRestrictedCellLabel,
  resolveRelationEndpointAccess,
} from './access-state';
import {
  findDatabaseByBlockId,
  getCrossDocRowDisplayCached,
} from './database-discovery-bridge';
import {
  getDatabaseDataSource,
  getRowDisplayValueFromDataSource,
} from './row-projection';

// Property Type Factory
export const relationPropertyType = propertyType('relation');

// Zod Schema fuer Property Data (Column-Konfiguration)
// E013-S002: Added targetDocId for cross-doc relations
export const RelationPropertyDataSchema = zod.object({
  targetDocId: zod.string().optional(), // E013: Doc ID for cross-doc relations (optional for backward compat)
  targetDatabaseId: zod.string(),
  targetFieldId: zod.string(),
  cardinality: zod.enum(['one', 'many']),
  inverseFieldId: zod.string().optional(),
  inverseCardinality: zod.enum(['one', 'many']).optional(), // Cardinality of the inverse column
});

export type RelationPropertyData = zod.infer<typeof RelationPropertyDataSchema>;

// Cell Value Schema - IMMER string[] (ADR-001)
export const RelationCellRawValueSchema = zod.array(zod.string()).nullable();
export const RelationCellJsonValueSchema = zod.array(zod.string()).nullable();

export type RelationCellRawValue = zod.infer<typeof RelationCellRawValueSchema>;
export type RelationCellJsonValue = zod.infer<
  typeof RelationCellJsonValueSchema
>;

function isDatabaseBlockDataSource(
  dataSource: unknown
): dataSource is DatabaseBlockDataSource {
  return (
    !!dataSource &&
    typeof dataSource === 'object' &&
    'doc' in dataSource &&
    typeof (dataSource as DatabaseBlockDataSource).cellValueGet === 'function'
  );
}

function relationValueToString({
  value,
  data,
  dataSource,
}: {
  value: RelationCellRawValue;
  data: RelationPropertyData;
  dataSource?: unknown;
}): string {
  if (!value || value.length === 0) {
    return '';
  }

  const targetDatabaseId = data.targetDatabaseId;
  const targetFieldId = data.targetFieldId;
  if (!targetDatabaseId || !targetFieldId) {
    return value.join(', ');
  }

  const labels: string[] = [];
  const access = resolveRelationEndpointAccess({
    targetDatabaseId,
    targetDocId: data.targetDocId,
  });
  if (access.state === 'restricted') {
    return getRelationRestrictedCellLabel();
  }

  const targetDatabase =
    access.database ?? findDatabaseByBlockId(targetDatabaseId);

  if (
    targetDatabase &&
    isDatabaseBlockDataSource(dataSource) &&
    targetDatabase.docId === dataSource.doc.id
  ) {
    const targetDataSource = getDatabaseDataSource(
      dataSource.doc,
      targetDatabaseId
    );
    if (targetDataSource) {
      for (const rowId of value) {
        const label = getRowDisplayValueFromDataSource(
          targetDataSource,
          rowId,
          targetFieldId
        );
        if (label) {
          labels.push(label);
        }
      }
    }
  } else if (targetDatabase) {
    for (const rowId of value) {
      const label = getCrossDocRowDisplayCached(
        targetDatabase.docId,
        targetDatabaseId,
        rowId
      );
      if (label) {
        labels.push(label);
      }
    }
  }

  return labels.length > 0 ? labels.join(', ') : value.join(', ');
}

// Model Config
export const relationPropertyModelConfig = relationPropertyType.modelConfig({
  name: 'Relation',
  propertyData: {
    schema: RelationPropertyDataSchema,
    default: () => ({
      targetDatabaseId: '',
      targetFieldId: '',
      cardinality: 'one' as const,
    }),
  },
  rawValue: {
    schema: RelationCellRawValueSchema,
    default: () => null as RelationCellRawValue,
    fromString: ({ value }) => ({
      value: value ? [value] : null,
    }),
    toString: relationValueToString,
    toJson: ({ value }) => value,
    fromJson: ({ value }) => value,
  },
  jsonValue: {
    schema: RelationCellJsonValueSchema,
    type: () => t.array.instance(t.string.instance()),
    isEmpty: ({ value }) => !value || value.length === 0,
  },
});
